import React from 'react';
import styled from 'styled-components';

//COMPONENTS
import Divider from 'components/ui/Divider';

//HELPERS
import { featureEnabled } from 'helpers/ToggleBot';
import camelCase from 'lodash/camelCase';
import { deepDiver } from 'helpers/utilities';
import { getTranslatedString } from 'helpers/i18n';

const notificationPreferenceString = (preferences) => {
  var arr = [];

  if (preferences.use_email) {
    arr.push(getTranslatedString('landlordPortal.tenantOverview.email'));
  }
  if (preferences.use_sms) {
    arr.push(getTranslatedString('landlordPortal.tenantOverview.sms'));
  }
  if (preferences.use_push && featureEnabled('push_notifications')) {
    arr.push(getTranslatedString('landlordPortal.tenantOverview.push'));
  }

  if (arr.length === 0) {
    return getTranslatedString('landlordPortal.tenantOverview.none');
  }
  return arr.join(', ');
};

const StyledBody = styled.div`
  margin: 15px 15px 25px;
`;

const StyledDivider = styled(Divider)`
  margin-bottom: 5px;
  margin-top: 5px;
  height: 2px;
  background-color: rgba(0, 0, 0, 0.06);
`;

const StyledBoldRight = styled.div`
  float: right;
  font-weight: bold;
`;

const StyledBodyHeader = styled.div`
  font-family: Circular-Bold;
  font-size: 16px;
  margin-bottom: 10px;
`;

const StyledBodyVerbiage = styled.div`
  margin-bottom: 25px;
`;

const NotificationPreferenceRow = ({ notificationType, notificationPreferences, showDivider }) => {
  return (
    <div>
      {getTranslatedString(`landlordPortal.tenantOverview.${camelCase(notificationType)}`)}
      <StyledBoldRight>
        {notificationPreferenceString(notificationPreferences[notificationType])}
      </StyledBoldRight>
      {showDivider && <StyledDivider />}
    </div>
  );
};

const NoticeOfEntryAgreementRow = ({ agreed }) => {
  const verbiage = agreed ? 'yes' : 'no';
  return (
    <div>
      {getTranslatedString('landlordPortal.tenantOverview.noticeOfEntry')}
      <StyledBoldRight>
        {getTranslatedString(`landlordPortal.tenantOverview.${verbiage}`)}
      </StyledBoldRight>
      <StyledDivider />
    </div>
  );
};

const NotificationPreferencesOverview = ({ tenantDetails }) => {
  if (
    !tenantDetails.invitation_accepted_at ||
    !tenantDetails.notification_preferences ||
    tenantDetails.lead_inactive
  ) {
    return (
      <StyledBody>
        <StyledBodyHeader>
          {getTranslatedString('landlordPortal.tenantOverview.NotificationPreferences')}
        </StyledBodyHeader>
        <StyledBodyVerbiage>
          {getTranslatedString('landlordPortal.tenantOverview.notRegisteredPreferences')}
        </StyledBodyVerbiage>
      </StyledBody>
    );
  }

  const showNoticeOfEntry =
    deepDiver(tenantDetails, 'notification_preferences.maintenance_requests.visible') &&
    featureEnabled(
      'ui.residents.create_service_request.notice_of_entry_consent.permission_to_enter_non_24hour'
    );

  return (
    <StyledBody>
      <StyledBodyHeader>
        {getTranslatedString('landlordPortal.tenantOverview.NotificationPreferences')}
      </StyledBodyHeader>
      {deepDiver(tenantDetails, 'notification_preferences.building_communications.visible') && (
        <NotificationPreferenceRow
          notificationType={'building_communications'}
          notificationPreferences={tenantDetails.notification_preferences}
        />
      )}
      {deepDiver(tenantDetails, 'notification_preferences.payments.visible') && (
        <NotificationPreferenceRow
          notificationType={'payments'}
          notificationPreferences={tenantDetails.notification_preferences}
        />
      )}
      {deepDiver(tenantDetails, 'notification_preferences.maintenance_requests.visible') && (
        <NotificationPreferenceRow
          notificationType={'maintenance_requests'}
          notificationPreferences={tenantDetails.notification_preferences}
        />
      )}
      {showNoticeOfEntry && <NoticeOfEntryAgreementRow agreed={tenantDetails.notice_by_email} />}
      {deepDiver(tenantDetails, 'notification_preferences.amenities.visible') && (
        <NotificationPreferenceRow
          notificationType={'amenities'}
          notificationPreferences={tenantDetails.notification_preferences}
        />
      )}
      {deepDiver(tenantDetails, 'notification_preferences.promotions.visible') && (
        <NotificationPreferenceRow
          notificationType={'promotions'}
          notificationPreferences={tenantDetails.notification_preferences}
        />
      )}
      {deepDiver(tenantDetails, 'notification_preferences.surveys.visible') && (
        <NotificationPreferenceRow
          notificationType={'surveys'}
          notificationPreferences={tenantDetails.notification_preferences}
          showDivider={false}
        />
      )}
    </StyledBody>
  );
};

NotificationPreferenceRow.defaultProps = {
  showDivider: true,
};

export default NotificationPreferencesOverview;
